import { type OrderbookEntry } from "#/types/orderbook";

interface MaxAvailableSizeOrderbook {
  bids?: OrderbookEntry[];
  asks?: OrderbookEntry[];
  isBuy: boolean;
}

interface MaxAvailableSizeOrderbookSide {
  depth: [number, number][]; // [price, size]
  isBuy: boolean;
}

type MaxAvailableSizeValues =
  | MaxAvailableSizeOrderbook
  | MaxAvailableSizeOrderbookSide;

const isOrderbookInput = (
  input: MaxAvailableSizeValues,
): input is MaxAvailableSizeOrderbook => {
  return "bids" in input && "asks" in input && "isBuy" in input;
};

export interface CalculatedMaxAvailableSize {
  isValid: boolean;
  maxAvailableSize: number;
  hasEnoughLiquidity: boolean;
}

export interface CalculateMaxAvailableSizeOptions {
  values?: MaxAvailableSizeValues;
  usersSize?: number;
  usersPrice?: number;
}

export const maxAvailableSizeDefaultValues: CalculatedMaxAvailableSize = {
  isValid: false,
  maxAvailableSize: 0,
  hasEnoughLiquidity: false,
};

export const calculateMaxAvailableSize = ({
  values,
  usersSize = 0,
  usersPrice,
}: CalculateMaxAvailableSizeOptions) => {
  if (!usersPrice) return maxAvailableSizeDefaultValues;

  if (!values) return maxAvailableSizeDefaultValues;

  const depthOfMarket = isOrderbookInput(values)
    ? values[values.isBuy ? "asks" : "bids"]
    : values.depth?.map(([price, size]) => ({ price, size }) as OrderbookEntry);

  if (!depthOfMarket?.length) return maxAvailableSizeDefaultValues;

  const matchingEntries = depthOfMarket
    .toSorted((a, b) => (values.isBuy ? a.price - b.price : b.price - a.price))
    .filter((entry) => {
      return values.isBuy
        ? entry.price <= usersPrice
        : entry.price >= usersPrice;
    });

  if (!matchingEntries.length) return maxAvailableSizeDefaultValues;

  const maxAvailableSize = matchingEntries.reduce(
    (acc, curr) => acc + curr.size,
    0,
  );

  const bestPrice = matchingEntries.at(0)?.price ?? NaN;

  const isPriceAvailable = values.isBuy
    ? bestPrice <= usersPrice
    : bestPrice >= usersPrice;
  const isAmountAvailable = maxAvailableSize >= usersSize;

  return {
    isValid: true,
    maxAvailableSize,
    hasEnoughLiquidity: isPriceAvailable && isAmountAvailable,
  };
};
